/**
 * Seções XML do system prompt do agente.
 *
 * O rodapé do editor (BehaviorTab) lista essas seções como âncoras: clicar
 * rola o Monaco até a abertura da tag correspondente. Por isso guardamos,
 * além do nome, a POSIÇÃO de cada abertura no texto (offset de caractere) —
 * o Monaco converte offset → linha/coluna com `model.getPositionAt()`, que
 * continua certo com `\r\n`, com quebra de linha automática (`wordWrap`) e
 * com o prompt sendo editado, coisa que contar `\n` na mão não garante.
 */

/** Uma seção detectada, com todas as posições em que a tag abre. */
export interface PromptSection {
  /** Nome da tag, sem o `<` (ex.: `persona`). */
  name: string;
  /**
   * Offset (em caracteres) do NOME da tag em cada abertura, na ordem em que
   * aparecem no prompt. Sempre tem ao menos um item.
   */
  offsets: number[];
}

/**
 * Abertura de tag XML: `<` seguido do nome e de espaço ou `>`.
 *
 * É o MESMO padrão que o rodapé já usava antes das âncoras — mantido igual
 * de propósito, pra lista exibida não mudar. Fechamentos (`</x>`), diretivas
 * (`<!--`, `<?xml`) e auto-fechadas (`<br/>`) continuam de fora.
 */
const OPEN_TAG_SOURCE = '<([a-zA-Z_][\\w-]*)(?=[\\s>])';

/**
 * Extrai as seções do prompt, sem repetir nome, na ordem da primeira aparição.
 *
 * Função pura: não depende do editor nem do React.
 */
export function detectPromptSections(prompt: string): PromptSection[] {
  const sections: PromptSection[] = [];
  const byName = new Map<string, PromptSection>();
  // Instância local: `lastIndex` de regex global é estado mutável, e um
  // literal compartilhado no módulo faria a segunda chamada começar do meio.
  const regex = new RegExp(OPEN_TAG_SOURCE, 'g');

  let match: RegExpExecArray | null = regex.exec(prompt);
  while (match !== null) {
    const name = match[1];
    if (name) {
      // +1 pula o `<`: o cursor deve cair no nome da tag, não no sinal.
      const offset = match.index + 1;
      const existing = byName.get(name);
      if (existing) {
        existing.offsets.push(offset);
      } else {
        const section: PromptSection = { name, offsets: [offset] };
        byName.set(name, section);
        sections.push(section);
      }
    }
    match = regex.exec(prompt);
  }

  return sections;
}
